/**
 * Avatar entouré de son aura électrique : halo, flammes et éclairs (canvas,
 * `aura-engine.ts`) + anneau qui grésille (SVG), dans la teinte de la carte.
 * La PUISSANCE (0..100, `cardAuraScore` = maîtrise × part d'HORA) module
 * chaque couche en continu ; à 0 il ne reste que le cercle de l'avatar.
 * L'avatar reste lisible : seuls de fins arcs passent devant son bord.
 */

import { useEffect, useId, useRef, type ReactNode } from 'react';
import { cn, usePrefersReducedMotion } from '@jeraisonne/ui';
import { auraParams, createAuraEngine, type AuraEngine } from './aura-engine';
import './avatar-aura.css';

/** Puissance bornée et arrondie (0..100). */
export function clampAuraPower(power: number): number {
  return Number.isFinite(power) ? Math.round(Math.min(100, Math.max(0, power))) : 0;
}

/** Graines successives du bruit de l'anneau (grésillement, en boucle). */
const RING_SEEDS = '1;7;3;12;5;9;2;14;6;11';

export function AvatarAura({
  power,
  color,
  children,
  className,
}: {
  /** Puissance 0..100 (`cardAuraScore`). */
  power: number;
  /** Teinte de la carte (hex). */
  color: string;
  /** L'avatar (centré, diamètre = la moitié du conteneur). */
  children: ReactNode;
  className?: string;
}) {
  const shown = clampAuraPower(power);
  const reduced = usePrefersReducedMotion();
  const backRef = useRef<HTMLCanvasElement>(null);
  const frontRef = useRef<HTMLCanvasElement>(null);
  const engine = useRef<AuraEngine | null>(null);
  const filterId = `jr-aura-zap-${useId().replace(/:/g, '')}`;
  const { ringZap } = auraParams(power);

  // Le moteur vit tant que le composant est monté (relancé si la préférence
  // « réduire les animations » change) ; puissance et teinte passent à chaud.
  useEffect(() => {
    if (!backRef.current || !frontRef.current) return;
    engine.current = createAuraEngine(backRef.current, frontRef.current, {
      power,
      color,
      reduced,
    });
    return () => {
      engine.current?.destroy();
      engine.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- power/color : effets dédiés
  }, [reduced]);

  useEffect(() => {
    engine.current?.setPower(power);
  }, [power]);

  useEffect(() => {
    engine.current?.setColor(color);
  }, [color]);

  return (
    <div
      role="img"
      aria-label={`Aura ${shown} %`}
      className={cn('relative flex h-40 w-40 items-center justify-center', className)}
      data-aura-power={shown}
    >
      <canvas
        ref={backRef}
        aria-hidden="true"
        className="pointer-events-none absolute -inset-[30%] h-[160%] w-[160%]"
      />
      <div className="relative z-[1]">{children}</div>
      <svg
        aria-hidden="true"
        viewBox="-100 -100 200 200"
        className="jr-aura-svg pointer-events-none absolute inset-0 z-[2] h-full w-full"
      >
        {ringZap > 0 && (
          <defs>
            <filter id={filterId} x="-30%" y="-30%" width="160%" height="160%">
              <feTurbulence
                type="fractalNoise"
                baseFrequency="0.45"
                numOctaves={2}
                seed={1}
                result="n"
              >
                {!reduced && (
                  <animate
                    attributeName="seed"
                    values={RING_SEEDS}
                    dur="0.7s"
                    calcMode="discrete"
                    repeatCount="indefinite"
                  />
                )}
              </feTurbulence>
              <feDisplacementMap
                in="SourceGraphic"
                in2="n"
                scale={ringZap}
                xChannelSelector="R"
                yChannelSelector="G"
                result="d"
              />
              <feGaussianBlur in="d" stdDeviation="1.2" result="b" />
              <feMerge>
                <feMergeNode in="b" />
                <feMergeNode in="d" />
              </feMerge>
            </filter>
          </defs>
        )}
        <g filter={ringZap > 0 ? `url(#${filterId})` : undefined}>
          <circle r={51.5} fill="none" stroke={color} strokeWidth={3} />
          {ringZap > 0 && (
            <circle r={51.5} fill="none" stroke="#fff" strokeWidth={1} opacity={0.85} />
          )}
        </g>
      </svg>
      <canvas
        ref={frontRef}
        aria-hidden="true"
        className="pointer-events-none absolute -inset-[30%] z-[3] h-[160%] w-[160%]"
      />
    </div>
  );
}
